import { ArrowDown, Plus } from 'lucide-react'
import { useState, type ClipboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { CarrierSelect } from '@/features/cases/components/CarrierSelect'
import { CarrierPaymentSheet } from '@/features/finance/components/CarrierPaymentSheet'
import { cn } from '@/lib/cn'
import { unitKindTitle } from '@/lib/legUnits'
import type { CaseLeg, Money, UnitKind } from '@/types/domain'

import {
  MAX_LEG_UNITS,
  RATE_CURRENCIES,
  appendRow,
  countedRows,
  fillNewRows,
  materialize,
  pasteRows,
  pristineRow,
  removeRow,
  rowsOfKind,
  updateRow,
  type RowDefaults,
  type UnitRow,
} from './legRows'
import { columnLabel, visibleColumns, type UnitColumn } from './unitKinds'
import { UnitCard, UnitGridRow, UnitRowSheet, type RowAbilities } from './UnitTableRows'
import { useTableFits } from './useTableFits'

/* One unit card — «Фуры», «Контейнеры», «Вагоны», «Авианакладные»,
 * «Коносаменты», «Другое» — under «Плечо целиком», and the place a leg's units
 * of that kind, their carriers and rates are entered (S-10's «Фуры и ставки»,
 * one card per kind since S-18b). The page draws the cards its mode implies
 * plus every kind that holds a unit (`unitKinds.shownUnitKinds`).
 *
 * ── A grid of inline inputs, and nothing in it writes ───────────────────────
 *
 * One row per unit of this kind, with this kind's own columns
 * (`UNIT_COLUMNS`); two blank rows always wait underneath with the defaults
 * drawn faintly, so "add a row" is never a step; a list of numbers pasted into
 * a number cell becomes rows. The rows are the PAGE's draft — one list across
 * every card (`legRows.ts` holds every rule about them): the page's one save
 * bar sends them as the leg PUT's `units` and their rates as one batch of
 * planned cost lines, and reconciles them with what each write answers.
 *
 * ── «Новые строки:» ─────────────────────────────────────────────────────────
 *
 * The carrier, rate and currency a new row of this card is born with. The
 * carrier IS the leg's own (the default a new unit takes, stored on the leg as
 * before); the rate and currency are the desk's for this sitting, per card.
 * «↓ всем» copies one of them into every NEW row of this card — never into a
 * saved unit, whose carrier and rate are facts somebody already agreed.
 *
 * ── Rates, once; the remainder, served ──────────────────────────────────────
 *
 * A new unit's rate, or a saved unit's with no per-unit plan yet, is an
 * input; a unit that already has one shows it read-only — that plan is an
 * AD-4 row the money screen edits, and an input here would write a second.
 * None of it exists for a reader who may not write money. «Остаток» is
 * `perUnit[].remaining`, read-only, for every reader (LegWholeCard's rule: the
 * figure reaches Sales, no identity does).
 *
 * ── Who sees whom ───────────────────────────────────────────────────────────
 *
 * The carrier, driver and phone columns exist only for a session that is shown
 * agent identity (`seesAgentIdentity`). A column of «скрыто» is still a column
 * that advertises what is withheld.
 *
 * ── A phone gets cards ──────────────────────────────────────────────────────
 *
 * Below `md` an editable grid would scroll sideways, which the README forbids:
 * each unit is a card, and editing one opens a bottom sheet.
 */

/** Each column's share of a fixed-layout grid; a card with fewer columns
 *  spreads the same shares over its own. */
const COLUMN_WIDTH: Record<UnitColumn, string> = {
  number: 'w-[15%]',
  trailer: 'w-[12%]',
  seal: 'w-[12%]',
  unitType: 'w-[9%]',
  driver: 'w-[14%]',
  phone: 'w-[12%]',
  carrier: 'w-[17%]',
  weight: 'w-[9%]',
  rate: 'w-[16%]',
  remaining: 'w-[11%]',
}

export function UnitsCard({
  caseId,
  leg,
  kind,
  rows,
  onRowsChange,
  defaults,
  onDefaultsChange,
  showIdentity,
  canRate,
  canPay,
  readOnly,
  planned,
  moneyHeld,
  bookedRate,
  heldRate,
  remaining,
  problems,
  rowErrors,
  rateMissingCurrency,
  notice,
}: {
  caseId: string
  /** The saved leg; absent on `legs/new`, where there is nothing to pay yet. */
  leg?: CaseLeg
  /** The card's kind — its title, its columns, and the rows it shows. */
  kind: UnitKind
  /** EVERY row of the leg's draft: the card edits its own kind's and hands
   *  the whole list back, and the cap is counted across all of them. */
  rows: UnitRow[]
  onRowsChange: (rows: UnitRow[]) => void
  defaults: RowDefaults
  onDefaultsChange: (defaults: RowDefaults) => void
  showIdentity: boolean
  /** Rate inputs, rate defaults and the batch exist at all. */
  canRate: boolean
  canPay: boolean
  readOnly: boolean
  /** Units that already have a per-unit planned line. */
  planned: ReadonlySet<string>
  /** Units that carry money and cannot be removed (0056). */
  moneyHeld: ReadonlySet<string>
  bookedRate: (unitId: string) => Money | undefined
  /** The rate a failed press is holding for a saved unit — shown read-only
   *  in place of the row's rate input until it lands or is discarded. */
  heldRate?: (unitId: string) => Money | undefined
  /** The served `perUnit[].remaining` of a saved unit. */
  remaining: (unitId: string) => Money | undefined
  problems: ReadonlyMap<string, { rate: boolean; weight: boolean }>
  /** The last leg write's served `units.<i>.<field>` codes, by row key. */
  rowErrors?: ReadonlyMap<string, ReadonlyArray<{ field: string; code: string }>>
  /** A batch refused with `rate-missing` in this currency. */
  rateMissingCurrency?: string
  /** Record-level sentences about these rows, above them. */
  notice?: ReactNode
}) {
  const { t } = useTranslation()
  const fits = useTableFits()

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const [pasteRefused, setPasteRefused] = useState<{ pasted: number; room: number } | null>(null)
  const [payingUnitId, setPayingUnitId] = useState<string | null>(null)
  /** The phone sheet: the row it edits, or a new one for «+ Фура». */
  const [editing, setEditing] = useState<{ row: UnitRow; index: number; isNew: boolean } | null>(null)

  const own = rowsOfKind(rows, kind)
  const count = countedRows(own)
  const full = countedRows(rows) >= MAX_LEG_UNITS
  const realRows = own.filter((row) => !row.pristine)
  /* A blank row is an input; a reader with nothing to type into gets the
     units alone. */
  const shown = fits && !readOnly ? own : realRows
  const title = unitKindTitle(t, kind)

  const abilitiesOf = (row: UnitRow): RowAbilities => {
    const hasPlan = row.id !== '' && planned.has(row.id)
    const currency = (row.pristine ? defaults.currency : row.currency) || 'USD'
    return {
      showIdentity,
      ratable: canRate && !hasPlan,
      bookedRate: hasPlan ? bookedRate(row.id) : undefined,
      heldRate: row.id !== '' && !hasPlan ? heldRate?.(row.id) : undefined,
      remaining: row.id !== '' ? remaining(row.id) : undefined,
      moneyHeld: row.id !== '' && moneyHeld.has(row.id),
      canPay: canPay && leg?.money !== undefined,
      readOnly,
      rateMissing: rateMissingCurrency !== undefined && currency === rateMissingCurrency && !row.pristine,
    }
  }

  const change = (key: string, patch: Partial<UnitRow>) => {
    setPasteRefused(null)
    onRowsChange(updateRow(rows, key, patch, defaults))
  }

  const paste = (key: string) => (event: ClipboardEvent<HTMLInputElement>) => {
    const result = pasteRows(rows, key, event.clipboardData.getData('text'), defaults)
    if (result === null) return
    event.preventDefault()
    if ('refused' in result) {
      setPasteRefused(result.refused)
      return
    }
    setPasteRefused(null)
    onRowsChange(result.rows)
  }

  const toggle = (key: string) => {
    const next = new Set(expanded)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setExpanded(next)
  }

  const columns = visibleColumns(kind, showIdentity).map((column) => ({
    key: column,
    label: columnLabel(t, kind, column),
    width: COLUMN_WIDTH[column],
    numeric: column === 'weight' || column === 'rate' || column === 'remaining',
    fill:
      column === 'carrier'
        ? ('carrier' as const)
        : column === 'rate' && canRate
          ? ('rate' as const)
          : undefined,
  }))

  const fillAll = (fill: 'carrier' | 'rate') =>
    onRowsChange(
      fillNewRows(
        rows,
        kind,
        fill === 'carrier'
          ? { carrierId: defaults.carrierId, carrierName: defaults.carrierName }
          : { rate: defaults.rate, currency: defaults.currency },
      ),
    )

  return (
    <Card>
      <CardHeader
        className="flex-wrap gap-3 px-5 pt-5"
        action={
          readOnly || !(showIdentity || canRate) ? undefined : (
            /* «Новые строки:» — what a new row is born with. The carrier is the
               leg's own default; the rate and currency are this sitting's. */
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-text-muted text-[13px]">
                {t('cases.legs.vehiclesTable.newRows')}
              </span>
              {showIdentity ? (
                <div className="w-56">
                  <CarrierSelect
                    size="sm"
                    value={defaults.carrierId}
                    name={defaults.carrierName}
                    aria-label={t('cases.legs.units.defaultCarrier', { kind: title })}
                    onChange={(carrier) =>
                      onDefaultsChange({ ...defaults, carrierId: carrier.id, carrierName: carrier.name })
                    }
                  />
                </div>
              ) : null}
              {canRate ? (
                <>
                  <Input
                    inputSize="sm"
                    inputMode="decimal"
                    className="asa-num w-28"
                    value={defaults.rate}
                    aria-label={t('cases.legs.units.defaultRate', { kind: title })}
                    onChange={(event) => onDefaultsChange({ ...defaults, rate: event.target.value })}
                  />
                  <Select
                    selectSize="sm"
                    className="w-[84px]"
                    value={defaults.currency}
                    onChange={(currency) => onDefaultsChange({ ...defaults, currency })}
                    options={RATE_CURRENCIES.map((code) => ({ value: code, label: code }))}
                    aria-label={t('cases.legs.units.defaultCurrency', { kind: title })}
                  />
                </>
              ) : null}
            </div>
          )
        }
      >
        <CardTitle className="flex flex-wrap items-baseline gap-3 text-[18px] font-normal">
          {title}
          {/* This card's units; the cap (`MAX_LEG_UNITS`, the server's
              `maxLegUnits`) is the leg's, every kind together, and is said
              when it is reached. */}
          <span className="text-text-muted asa-num text-[13px] font-light">{count}</span>
        </CardTitle>
      </CardHeader>

      <CardBody className="flex flex-col gap-3 px-0 pt-4 pb-0">
        {notice ? <div className="flex flex-col gap-3 px-5">{notice}</div> : null}

        {pasteRefused ? (
          <p role="alert" className="text-negative px-5 text-[12.5px] leading-snug">
            {t('cases.legs.vehiclesTable.pasteRefused', {
              count: pasteRefused.pasted,
              room: pasteRefused.room,
              max: MAX_LEG_UNITS,
            })}
          </p>
        ) : null}

        {fits ? (
          /* `relative` for the README's reason: the actions header is
             `sr-only`, absolutely positioned, and must not escape this box. */
          <div className="asa-scroll relative">
            <table aria-label={title} className="w-full table-fixed border-collapse text-left">
              <thead>
                <tr className="border-line border-b">
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className={cn(
                        'text-text-muted px-1.5 pb-2 text-[11px] font-normal tracking-wide uppercase first:pl-5',
                        column.width,
                        column.numeric && 'text-right',
                      )}
                    >
                      <span className={cn('inline-flex items-center gap-1.5', column.numeric && 'justify-end')}>
                        {column.label}
                        {column.fill && !readOnly ? (
                          <button
                            type="button"
                            onClick={() => fillAll(column.fill!)}
                            className="text-text-link hover:text-text-link-hover inline-flex cursor-pointer items-center gap-0.5 normal-case"
                            aria-label={t(
                              column.fill === 'carrier'
                                ? 'cases.legs.units.fillCarrier'
                                : 'cases.legs.units.fillRate',
                              { kind: title },
                            )}
                          >
                            <ArrowDown size={11} aria-hidden />
                            {t('cases.legs.vehiclesTable.fillAll')}
                          </button>
                        ) : null}
                      </span>
                    </th>
                  ))}
                  <th scope="col" className="w-[96px] pr-5 pb-2">
                    <span className="sr-only">{t('cases.legs.vehiclesTable.groupedColumns.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row, index) => (
                  <UnitGridRow
                    key={row.key}
                    row={row}
                    index={index}
                    defaults={defaults}
                    abilities={abilitiesOf(row)}
                    expanded={expanded.has(row.key)}
                    problems={problems.get(row.key) ?? { rate: false, weight: false }}
                    errors={rowErrors?.get(row.key)}
                    onChange={(patch) => change(row.key, patch)}
                    onPaste={paste(row.key)}
                    onRemove={() => onRowsChange(removeRow(rows, row.key))}
                    onToggle={() => toggle(row.key)}
                    onPay={() => setPayingUnitId(row.id)}
                  />
                ))}
              </tbody>
            </table>
            {!readOnly ? (
              <p className="text-text-muted px-5 py-3 text-[12.5px] leading-snug font-light">
                {full
                  ? t('cases.legs.vehiclesTable.full', { max: MAX_LEG_UNITS })
                  : t('cases.legs.vehiclesTable.pasteHint')}
              </p>
            ) : shown.length === 0 ? (
              <p className="text-text-muted px-5 py-4 text-[13px]">{t('cases.legs.units.empty')}</p>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-2 px-4 pb-4">
            {realRows.length === 0 ? (
              <p className="text-text-muted text-[13px]">{t('cases.legs.units.empty')}</p>
            ) : (
              <ul aria-label={title} className="flex flex-col gap-2">
                {realRows.map((row, index) => (
                  <UnitCard
                    key={row.key}
                    row={row}
                    index={index}
                    defaults={defaults}
                    abilities={abilitiesOf(row)}
                    errors={rowErrors?.get(row.key)}
                    onOpen={() => setEditing({ row, index, isNew: false })}
                  />
                ))}
              </ul>
            )}
            {readOnly ? null : full ? (
              <p className="text-text-muted text-[12.5px] leading-snug font-light">
                {t('cases.legs.vehiclesTable.full', { max: MAX_LEG_UNITS })}
              </p>
            ) : (
              <Button
                variant="secondary"
                iconLeft={Plus}
                onClick={() =>
                  setEditing({
                    row: materialize(pristineRow(kind), defaults),
                    index: realRows.length,
                    isNew: true,
                  })
                }
              >
                {t(`cases.legs.units.add.${kind}` as 'cases.legs.units.add.truck')}
              </Button>
            )}
          </div>
        )}
      </CardBody>

      {editing ? (
        <UnitRowSheet
          open
          row={editing.row}
          index={editing.index}
          defaults={defaults}
          abilities={abilitiesOf(editing.row)}
          onClose={() => setEditing(null)}
          onDone={(row) => {
            onRowsChange(
              editing.isNew
                ? appendRow(rows, row)
                : rows.map((current) => (current.key === row.key ? row : current)),
            )
            setEditing(null)
          }}
          onRemove={
            editing.isNew
              ? undefined
              : () => {
                  onRowsChange(removeRow(rows, editing.row.key))
                  setEditing(null)
                }
          }
        />
      ) : null}

      {/* The pay sheet the summary's «Оплатить перевозчику» opens, opened
          here already knowing the unit: its carrier chosen, its row ticked.
          Mounted only where the rows offer the act. */}
      {canPay && leg?.money ? (
        <CarrierPaymentSheet
          open={payingUnitId !== null}
          onClose={() => setPayingUnitId(null)}
          caseId={caseId}
          leg={leg}
          money={leg.money}
          defaultUnitId={payingUnitId ?? undefined}
        />
      ) : null}
    </Card>
  )
}
